import type { AccessRequestFieldConfig } from '@/constants/signup'
import { CURRENT_CLIENT } from '@/constants'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { yeshumanUiSans } from '@/components/landing/yeshuman/yeshuman-typography'
import { cn } from '@/lib/utils'

type FieldValues = {
  email: string
  name: string
  role: string
  company: string
  note: string
}

type FieldSetters = {
  setEmail: (value: string) => void
  setName: (value: string) => void
  setRole: (value: string) => void
  setCompany: (value: string) => void
  setNote: (value: string) => void
}

type AccessRequestFieldsProps = {
  variant: 'marketing' | 'auth' | 'marketingTheme'
  fields: AccessRequestFieldConfig[]
  values: FieldValues
  setters: FieldSetters
  submitting: boolean
  emailRequired?: boolean
  emailHint?: string
  emailLabel?: string
  emailPlaceholder?: string
  className?: string
  marketingFieldClassName?: string
  marketingSelectTriggerClassName?: string
  marketingSelectContentClassName?: string
  marketingSelectItemClassName?: string
}

function getFieldValue(id: string, values: FieldValues): string {
  if (id === 'name') return values.name
  if (id === 'role') return values.role
  if (id === 'company') return values.company
  if (id === 'note') return values.note
  return ''
}

function getFieldSetter(id: string, setters: FieldSetters): (value: string) => void {
  if (id === 'name') return setters.setName
  if (id === 'role') return setters.setRole
  if (id === 'company') return setters.setCompany
  if (id === 'note') return setters.setNote
  return () => undefined
}

function fieldLabel(field: AccessRequestFieldConfig): string | undefined {
  return field.label ?? field.placeholder
}

function FieldHint({ hint, variant }: { hint?: string; variant?: 'default' | 'marketingTheme' }) {
  if (!hint) return null
  return (
    <p
      className={cn(
        'text-xs',
        variant === 'marketingTheme' ? 'text-[var(--marketing-muted)]' : 'text-muted-foreground',
      )}
    >
      {hint}
    </p>
  )
}

const yeshumanLabelClass = cn(
  yeshumanUiSans,
  'text-xs font-medium tracking-[0.04em] text-[var(--marketing-muted)]',
)

export function AccessRequestFields({
  variant,
  fields,
  values,
  setters,
  submitting,
  emailRequired = true,
  emailHint,
  emailLabel,
  emailPlaceholder,
  className,
  marketingFieldClassName,
  marketingSelectTriggerClassName,
  marketingSelectContentClassName,
  marketingSelectItemClassName,
}: AccessRequestFieldsProps) {
  const isMarketing = variant === 'marketing'
  const isMarketingTheme = variant === 'marketingTheme'

  const renderTextField = (field: AccessRequestFieldConfig, fullWidth = false) => {
    const value = getFieldValue(field.id, values)
    const onChange = getFieldSetter(field.id, setters)
    if (isMarketingTheme) {
      return (
        <div key={field.id} className={cn('grid gap-2', fullWidth && 'col-span-full')}>
          {fieldLabel(field) ? (
            <Label htmlFor={`access-request-${field.id}`} className={yeshumanLabelClass}>
              {fieldLabel(field)}
            </Label>
          ) : null}
          <input
            id={`access-request-${field.id}`}
            type="text"
            name={field.id}
            placeholder={field.placeholder}
            aria-label={field.label ?? field.placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            required={field.required}
            disabled={submitting}
            className={cn(marketingFieldClassName, fullWidth && 'sm:col-span-2')}
          />
          <FieldHint hint={field.hint} variant="marketingTheme" />
        </div>
      )
    }
    if (isMarketing) {
      return (
        <input
          key={field.id}
          type="text"
          name={field.id}
          placeholder={field.placeholder}
          aria-label={field.label ?? field.placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={field.required}
          disabled={submitting}
          className={cn(marketingFieldClassName, fullWidth && 'sm:col-span-2')}
        />
      )
    }
    return (
      <div key={field.id} className={cn('grid gap-2', fullWidth && 'col-span-full')}>
        {fieldLabel(field) ? (
          <Label htmlFor={`access-request-${field.id}`}>{fieldLabel(field)}</Label>
        ) : null}
        <Input
          id={`access-request-${field.id}`}
          type="text"
          name={field.id}
          placeholder={field.placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={field.required}
          disabled={submitting}
        />
        <FieldHint hint={field.hint} />
      </div>
    )
  }

  const renderTextareaField = (field: AccessRequestFieldConfig) => {
    const value = getFieldValue(field.id, values)
    const onChange = getFieldSetter(field.id, setters)
    if (isMarketingTheme) {
      return (
        <div key={field.id} className="col-span-full grid gap-2">
          {fieldLabel(field) ? (
            <Label htmlFor={`access-request-${field.id}`} className={yeshumanLabelClass}>
              {fieldLabel(field)}
            </Label>
          ) : null}
          <textarea
            id={`access-request-${field.id}`}
            name={field.id}
            placeholder={field.placeholder}
            aria-label={field.label ?? field.placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            required={field.required}
            disabled={submitting}
            rows={3}
            className={cn(marketingFieldClassName, 'min-h-[6.25rem] resize-y')}
          />
          <FieldHint hint={field.hint} variant="marketingTheme" />
        </div>
      )
    }
    if (isMarketing) {
      return (
        <textarea
          key={field.id}
          name={field.id}
          placeholder={field.placeholder}
          aria-label={field.label ?? field.placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={field.required}
          disabled={submitting}
          rows={4}
          className={cn(marketingFieldClassName, 'min-h-[7.5rem] resize-y sm:col-span-2')}
        />
      )
    }
    return (
      <div key={field.id} className="col-span-full grid gap-2">
        {fieldLabel(field) ? (
          <Label htmlFor={`access-request-${field.id}`}>{fieldLabel(field)}</Label>
        ) : null}
        <Textarea
          id={`access-request-${field.id}`}
          name={field.id}
          placeholder={field.placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={field.required}
          disabled={submitting}
          rows={4}
        />
        <FieldHint hint={field.hint} />
      </div>
    )
  }

  const renderSelectField = (field: AccessRequestFieldConfig) => {
    const value = getFieldValue(field.id, values)
    const onChange = getFieldSetter(field.id, setters)
    const options = field.options ?? []
    if (isMarketing) {
      return (
        <div key={field.id} className="sm:col-span-2">
          <Select value={value || undefined} onValueChange={onChange} disabled={submitting}>
            <SelectTrigger
              className={cn(marketingSelectTriggerClassName, !value && 'text-white/70')}
              aria-label={field.placeholder ?? field.id}
            >
              <SelectValue placeholder={field.placeholder ?? 'Select…'} />
            </SelectTrigger>
            <SelectContent className={marketingSelectContentClassName}>
              {options.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  className={marketingSelectItemClassName}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )
    }
    return (
      <div key={field.id} className="grid gap-2">
        {fieldLabel(field) ? (
          <Label htmlFor={`access-request-${field.id}`}>{fieldLabel(field)}</Label>
        ) : null}
        <Select value={value || undefined} onValueChange={onChange} disabled={submitting}>
          <SelectTrigger id={`access-request-${field.id}`}>
            <SelectValue placeholder={field.placeholder ?? 'Select…'} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <FieldHint hint={field.hint} />
      </div>
    )
  }

  const renderEmailField = () => {
    const resolvedLabel = emailLabel ?? 'Email'
    const resolvedPlaceholder = emailPlaceholder ?? (isMarketing ? 'Email' : CURRENT_CLIENT.placeholderEmail || 'Email')
    if (isMarketing) {
      return (
        <input
          type="email"
          name="email"
          placeholder={resolvedPlaceholder}
          aria-label={resolvedLabel}
          value={values.email}
          onChange={(e) => setters.setEmail(e.target.value)}
          required={emailRequired}
          disabled={submitting}
          className={marketingFieldClassName}
        />
      )
    }
    if (isMarketingTheme) {
      return (
        <div className="grid gap-2">
          <Label htmlFor="access-request-email" className={yeshumanLabelClass}>
            {resolvedLabel}
          </Label>
          <input
            id="access-request-email"
            type="email"
            name="email"
            placeholder={resolvedPlaceholder}
            value={values.email}
            onChange={(e) => setters.setEmail(e.target.value)}
            required={emailRequired}
            disabled={submitting}
            className={marketingFieldClassName}
          />
          <FieldHint hint={emailHint} variant="marketingTheme" />
        </div>
      )
    }
    return (
      <div className="grid gap-2">
        <Label htmlFor="access-request-email">{resolvedLabel}</Label>
        <Input
          id="access-request-email"
          type="email"
          name="email"
          placeholder={resolvedPlaceholder}
          value={values.email}
          onChange={(e) => setters.setEmail(e.target.value)}
          required={emailRequired}
          disabled={submitting}
        />
        <FieldHint hint={emailHint} />
      </div>
    )
  }

  const nameField = fields.find((field) => field.id === 'name')
  const otherFields = fields.filter((field) => field.id !== 'name')

  return (
    <div className={className}>
      {isMarketing || isMarketingTheme ? (
        <>
          {nameField ? renderTextField(nameField) : null}
          {renderEmailField()}
        </>
      ) : (
        <>
          {renderEmailField()}
          {nameField ? renderTextField(nameField) : null}
        </>
      )}
      {otherFields.map((field) => {
        if (field.type === 'select') return renderSelectField(field)
        if (field.type === 'textarea') return renderTextareaField(field)
        return renderTextField(field, isMarketing || isMarketingTheme)
      })}
    </div>
  )
}
